import { useMemo } from 'react';
import type { FunnelDatum } from '@nivo/funnel';
import type {
  TModuleDisplayOption,
  TWidget,
  TWidgetData,
} from 'utility/types/dataProvider';

interface UseFunnelDataProps {
  widget: TWidget;
  data: TWidgetData;
  config: TModuleDisplayOption | undefined;
}

interface UseFunnelDataReturn {
  parsedData: FunnelDatum[];
  hasData: boolean;
  isNailShape: boolean;
  isHorizontal: boolean;
  showPercentDiff: boolean;
}

const getIsHorizontal = (widget: TWidget, config: TModuleDisplayOption | undefined) => {
  // Check the alternative way the API is sending the direction via widget.display instead of widget.module.
  // Example: CMSADMIN-1033 -> subscription.purchaseeventsfunnel.
  if ('direction' in widget?.display?.options) {
    return widget?.display?.options?.direction === 'horizontal';
  }

  return config?.direction === 'horizontal';
};

const getShowPercentDiff = (
  widget: TWidget,
  config: TModuleDisplayOption | undefined,
) => {
  // Similar to getIsHorizontal, check both widget.display and config for the percent_diff flag.
  if ('percent_diff' in widget?.display?.options) {
    return widget?.display?.options?.percent_diff;
  }

  return config?.percent_diff;
};

const getIsNailShape = (data: FunnelDatum[]) => {
  if (!data?.length) return false;
  return (data[0].value - data[data.length - 1].value) / data[0].value > 0.9;
};

export const useFunnelData = ({
  widget,
  data,
  config,
}: UseFunnelDataProps): UseFunnelDataReturn => {
  const parsedData = useMemo(() => {
    const results = data?.results;
    if (!results?.length || !widget?.module?.id) return [];

    const { label, value } = config || {};
    if (!label || !value) return [];

    const isHorizontal = getIsHorizontal(widget, config);

    // CMSADMIN-1033: Handle special case for horizontal funnels with nested data.
    if (isHorizontal) {
      // Flatten the nested user_data structure for purchase events funnel
      return results
        .map((result: any) => {
          const labelStr = String(result?.[label] ?? 'Unknown');

          // Format the label to be more human readable
          const formattedLabel = labelStr
            .replace(/_interest$/, '')
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (match: string) => match.toUpperCase());

          const _value = result?.[value] ?? 0;

          return {
            id: `${_value}-${labelStr}`,
            value: _value,
            label: formattedLabel,
          };
        })
        .sort((a: FunnelDatum, b: FunnelDatum) => b.value - a.value);
    } else {
      return results
        .map((result: any) => {
          const resultLabelStr = String(result[label] ?? 'Unknown');

          // Format the label to be more human readable.
          const formattedLabel = resultLabelStr
            .replace(/_interest$/, '')
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (match: string) => match.toUpperCase());

          return {
            id: `${result[value]}-${resultLabelStr}`,
            value: result[value],
            label: formattedLabel,
          };
        })
        .sort((a: FunnelDatum, b: FunnelDatum) => b.value - a.value); // Sort the results to ensure the funnel is in the correct order.
    }
  }, [config, data?.results, widget]);

  const hasData = !!parsedData?.length;

  const isNailShape = getIsNailShape(parsedData);

  const isHorizontal = getIsHorizontal(widget, config);
  const showPercentDiff = getShowPercentDiff(widget, config);

  return { parsedData, hasData, isNailShape, isHorizontal, showPercentDiff };
};
